// Web equivalent of Wellnotes/ViewModels/AuthViewModel.swift.
// Singleton reactive store: exposes currentUser, userData, isAuthenticated, isLoading.
// Attaches Firestore listeners on the user doc + devices/friends/sharedNotes subcollections.

import { reactive, readonly } from "vue";
import { onAuthStateChanged, signInWithCustomToken, signOut as fbSignOut } from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDocFromServer,
  getDocs,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import {
  auth,
  db,
  setAnalyticsUser,
  enableAnalytics,
  disableAnalytics,
  setTrackingEligible,
} from "../firebase";
import { deviceInfos, deviceLinkID } from "../utils/pairing";
import {
  callable,
  clearSeatPending,
  deviceFields,
  linkErrorReason,
  markSeatPending,
  takeSeatPending,
} from "../utils/linking";
import { attachNotesListeners, detachNotesListeners } from "./notesStore";
import { canTrack, getAgeBracket } from "../utils/trackingPolicy";

const state = reactive({
  currentUser: null,
  isAuthenticated: false,
  isLoading: true,
  userData: null,
  // Another device holds this free account (see claimSeat): the app shows
  // SeatLockOverlay until premium unlocks it or the reader signs out here.
  seatLock: null,
});

let userDocUnsub = null;
let devicesUnsub = null;
let friendsUnsub = null;
let sharedNotesUnsub = null;
let lastConnectionTimer = null;
let initialized = false;

let latestDevices = [];
let latestFriends = [];
let latestSharedNotes = [];

// Tracks the most recent authenticated UID so we can run a clean device-list
// cleanup if Firebase later flips the auth state to `null` (token revoked,
// cookies/IndexedDB wiped, account disabled, password changed elsewhere…).
let previousUid = null;
// Set to true only by `signOut()` so the auth-state change handler knows the
// imminent `null` transition is intentional and not a session expiry.
let isExplicitSignOut = false;
// Guards the auto-signout path so concurrent listener errors don't fire it
// multiple times in a row.
let autoSignOutInFlight = false;
// Tracks whether the devices listener has confirmed the local browser's
// device record exists. Once we've seen it, a later snapshot that drops it
// means another session (typically the iOS app's "End session" action)
// revoked us — we then sign the browser out.
let hasSeenLocalDeviceRecord = false;

function detachUserListeners() {
  if (userDocUnsub) {
    userDocUnsub();
    userDocUnsub = null;
  }
  if (devicesUnsub) {
    devicesUnsub();
    devicesUnsub = null;
  }
  if (friendsUnsub) {
    friendsUnsub();
    friendsUnsub = null;
  }
  if (sharedNotesUnsub) {
    sharedNotesUnsub();
    sharedNotesUnsub = null;
  }
  latestDevices = [];
  latestFriends = [];
  latestSharedNotes = [];
}

function stopLastConnectionTimer() {
  if (lastConnectionTimer) {
    clearInterval(lastConnectionTimer);
    lastConnectionTimer = null;
  }
}

function isoStringNoMs(date = new Date()) {
  // iOS DeviceView parses lastConnection with ISO8601DateFormatter() in its default
  // .withInternetDateTime mode, which rejects fractional seconds. Strip them.
  return date.toISOString().replace(/\.\d+Z$/, "Z");
}

async function upsertUserDeviceRecord(userID) {
  // Mirrors DeviceHelper.swift upsertUserDeviceRecord — simplified single-write upsert.
  try {
    const identity = await deviceInfos(userID);
    const deviceID = deviceLinkID();
    const ref = doc(db, "users", userID, "devices", deviceID);
    await setDoc(
      ref,
      {
        ...identity,
        deviceID,
        lastConnection: isoStringNoMs(),
      },
      { merge: true },
    );
    await pruneStaleBrowserSessions(userID, deviceID, identity.systemName);
  } catch (err) {
    console.warn("upsertUserDeviceRecord failed:", err);
  }
}

// A session this browser left behind (cleared storage, another origin such
// as localhost) keeps its own device doc, so the list shows the same browser
// twice. Live sessions refresh lastConnection every 5 minutes; a doc for the
// same browser that has been silent longer than this is a dead session.
// Live ones are kept: deleting a device doc signs that session out.
const STALE_SESSION_MS = 15 * 60 * 1000;

async function pruneStaleBrowserSessions(userID, currentID, browser) {
  if (!browser) return;
  const snap = await getDocs(
    query(collection(db, "users", userID, "devices"), where("systemName", "==", browser)),
  );
  const now = Date.now();
  const stale = snap.docs.filter((d) => {
    if (d.id === currentID) return false;
    const last = Date.parse(d.data().lastConnection || "");
    return !Number.isFinite(last) || now - last > STALE_SESSION_MS;
  });
  await Promise.all(stale.map((d) => deleteDoc(d.ref)));
}

function startLastConnectionTimer(userID) {
  stopLastConnectionTimer();
  upsertUserDeviceRecord(userID);
  lastConnectionTimer = setInterval(() => {
    upsertUserDeviceRecord(userID);
  }, 5 * 60 * 1000);
}

function applySubcollections() {
  if (!state.userData) return;
  if (latestDevices.length > 0) state.userData.devices = latestDevices;
  if (latestFriends.length > 0) state.userData.friends = latestFriends;
  if (latestSharedNotes.length > 0) state.userData.sharedNotes = latestSharedNotes;
}

/**
 * Detects whether a Firestore listener error indicates the user's auth
 * session has been invalidated (browser cookies expired, account disabled,
 * password changed elsewhere…). When it has, the listener fires with codes
 * that map to "permission-denied" or "unauthenticated".
 */
function isAuthLossError(err) {
  if (!err) return false;
  const code = err.code || "";
  return (
    code === "permission-denied" ||
    code === "unauthenticated" ||
    code === "auth/user-token-expired" ||
    code === "auth/invalid-user-token" ||
    code === "auth/user-disabled"
  );
}

/**
 * Runs the same cleanup as the explicit `signOut()` (delete this device's
 * record from the user's device list, detach listeners, clear local state,
 * Firebase signOut), then redirects to `/signin`. Best-effort: if the auth
 * token is already invalid the device delete will throw permission-denied,
 * which we swallow.
 */
async function forceSignOut(reason, expectedUid) {
  // Meant for one account: if the browser has moved to another meanwhile
  // (a merge followed), there is nothing to sign out of.
  if (expectedUid && (!auth.currentUser || auth.currentUser.uid !== expectedUid)) return;
  if (autoSignOutInFlight) return;
  autoSignOutInFlight = true;
  console.warn("Auth session expired — forcing clean sign-out:", reason);
  const uid = (auth.currentUser && auth.currentUser.uid) || previousUid;
  if (uid) {
    try {
      const deviceID = deviceLinkID();
      await deleteDoc(doc(db, "users", uid, "devices", deviceID));
    } catch (_) {
      // Auth may already be gone — Firestore will reject the write. Ignore.
    }
  }
  detachUserListeners();
  detachNotesListeners();
  stopLastConnectionTimer();
  try {
    localStorage.removeItem("userID");
  } catch (_) {
    // ignore
  }
  isExplicitSignOut = true;
  try {
    await fbSignOut(auth);
  } catch (_) {
    // ignore — auth might already be in a half-signed-out state
  }
  // Redirect users on a protected page back to sign-in. Using replace() so
  // the back button doesn't bounce them into a route that now 401s.
  if (typeof window !== "undefined") {
    const path = window.location.pathname || "";
    const isPublic = path === "/" || path.startsWith("/signin") ||
      path.startsWith("/terms") || path.startsWith("/privacy") ||
      path.startsWith("/legal") || path.startsWith("/help") ||
      path.startsWith("/compliance");
    if (!isPublic) {
      window.location.replace("/signin");
    }
  }
  // Reset the guard after a short delay so a future re-login can sign out
  // again normally.
  setTimeout(() => {
    autoSignOutInFlight = false;
  }, 2000);
}

// Records whether we've already sent the "you must be 13+" message for the
// current session so subsequent user-doc snapshots don't re-trigger it.
let under13SignOutInFlight = false;

function enforceAgePolicy(userData) {
  const age = userData && typeof userData.age === "number" ? userData.age : null;
  const eligible = canTrack(age);
  // Push the eligibility flag down into firebase.js so analytics is flipped
  // off at the SDK level for under-13 / kid-mode users, even if the SDK was
  // already initialised by an earlier consent click.
  setTrackingEligible(eligible).then(() => {
    // Once the gate has flipped to "eligible" we can finally bind the UID
    // to Analytics. setAnalyticsUser is a no-op until trackingEligible is
    // true, so it's safe to call unconditionally before this point too —
    // but doing it here means adults get the binding the instant their
    // user doc loads, not on the next page navigation.
    const uid = auth.currentUser ? auth.currentUser.uid : null;
    if (eligible && uid) {
      setAnalyticsUser(uid);
    }
  });

  if (age != null && getAgeBracket(age) === "under13" && !under13SignOutInFlight) {
    under13SignOutInFlight = true;
    // Use the existing forced-cleanup path so listeners detach, the device
    // doc is removed and we land back on /signin. We rely on the
    // pending-snackbar localStorage hand-off because the snackbar store is
    // wiped during navigation; SignIn.vue picks it up on mount.
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem("cogito_under13_block", "1");
      }
    } catch (_) {
      // ignore
    }
    forceSignOut("under-13 account");
  }
}

// ── Device seat ─────────────────────────────────────────────────────────
//
// A free account is used on one device at a time (premium lifts the limit).
// The server keeps which device holds it; this browser asks when the user
// document arrives, when premium goes away, and when the tab comes back
// after a while. Never locks anyone out on a failure: it just asks again
// later.
//
// Asked sparingly: a premium account has no limit, so it never asks; a
// browser answered "active" keeps that answer for a day (in localStorage,
// so a reload does not ask again), well within the month the server waits
// before taking an idle holder's seat. A locked browser, a new account, a
// sign-in or a merge ask right away.

const SEAT_REFRESH_MS = 24 * 60 * 60 * 1000;
const SEAT_LOCKED_REFRESH_MS = 30 * 60 * 1000;
const SEAT_ANSWER_KEY = "cogito_device_seat_answer_v1";
let seatFor = { uid: null, premium: null };
let seatInFlight = false;

function readSeatAnswer() {
  try {
    const raw = typeof localStorage !== "undefined" ? localStorage.getItem(SEAT_ANSWER_KEY) : null;
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed.uid === "string" && typeof parsed.at === "number" ? parsed : null;
  } catch (_) {
    return null;
  }
}

function rememberSeatAnswer(uid, locked) {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(SEAT_ANSWER_KEY, JSON.stringify({ uid, at: Date.now(), locked: !!locked }));
    }
  } catch (_) {
    // ignore
  }
}

function forgetSeatAnswer() {
  try {
    if (typeof localStorage !== "undefined") localStorage.removeItem(SEAT_ANSWER_KEY);
  } catch (_) {
    // ignore
  }
}

/** Whether the server has to be asked now. */
function seatIsDue() {
  const uid = seatFor.uid;
  if (!uid) return false;
  // How the session got here must reach the server, premium or not.
  if (takeSeatPending()) return true;
  // No limit to enforce.
  if (seatFor.premium) return false;
  const answer = readSeatAnswer();
  if (!answer || answer.uid !== uid) return true;
  const age = Date.now() - answer.at;
  // Locked: the overlay is not kept across reloads, so a reload asks again;
  // in the foreground, premium may have arrived meanwhile.
  if (answer.locked) return !state.seatLock || age > SEAT_LOCKED_REFRESH_MS;
  return age > SEAT_REFRESH_MS;
}
// Something changed while a claim was in flight (premium bought, another
// account): its answer is stale, so ask again when it lands.
let seatNeedsReclaim = false;

async function claimSeat() {
  const uid = seatFor.uid;
  if (!uid || !auth.currentUser || auth.currentUser.uid !== uid) return;
  if (seatInFlight) {
    seatNeedsReclaim = true;
    return;
  }
  seatInFlight = true;
  seatNeedsReclaim = false;
  const pending = takeSeatPending();
  const via = pending || "launch";
  try {
    const result = await callable("claimDeviceSeat")({ ...(await deviceFields()), via });
    if (!auth.currentUser || auth.currentUser.uid !== uid) return;
    // Used — unless a newer reason (a sign-in meanwhile) replaced it.
    if (pending && takeSeatPending() === pending) clearSeatPending();
    const data = (result && result.data) || {};
    rememberSeatAnswer(uid, data.status === "locked");
    if (seatNeedsReclaim && seatFor.premium) return;
    if (data.status === "locked") {
      const holder = data.holder || {};
      state.seatLock = { uid, holderName: holder.name || null, holderKind: holder.kind || "unknown" };
    } else {
      state.seatLock = null;
    }
  } catch (err) {
    if (linkErrorReason(err) === "account-merged") followMergeIfNeeded(uid);
    console.warn("claimDeviceSeat failed:", err);
  } finally {
    seatInFlight = false;
    if (seatNeedsReclaim) claimSeat();
  }
}

function updateSeat(uid, premium) {
  const wasPremium = seatFor.uid === uid && seatFor.premium === true;
  seatFor = { uid, premium };
  // Premium lifts the limit: no need to wait for the server to say so.
  if (premium && state.seatLock && state.seatLock.uid === uid) state.seatLock = null;
  // Premium went away: the seat matters again, ask now.
  if (wasPremium && !premium) forgetSeatAnswer();
  if (seatIsDue()) claimSeat();
}

function resetSeat() {
  seatFor = { uid: null, premium: null };
  forgetSeatAnswer();
  state.seatLock = null;
}

if (typeof document !== "undefined") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState !== "visible" || !seatFor.uid) return;
    if (seatIsDue()) claimSeat();
  });
}

/** Before a voluntary sign-out: frees the account for another device. */
async function releaseSeat() {
  if (!auth.currentUser) return;
  try {
    await callable("releaseDeviceSeat", { timeout: 8000 })({ deviceID: deviceLinkID() });
  } catch (err) {
    console.warn("releaseDeviceSeat failed:", err);
  }
}

// ── Following a merged account ──────────────────────────────────────────
//
// This browser is signed into an account that no longer exists because it
// was merged into another one: move to the survivor. Asked of the server,
// never the cache. Resolves to
//   "followed"   — this browser is no longer on `uid` (moved here, or
//                  already moved by another path: the merged code)
//   "not-merged" — the account was not merged
//   "error"      — could not tell (offline…): ask again later

let followingMerge = null;

function followMergeIfNeeded(uid) {
  if (followingMerge) return followingMerge;
  const stillOn = () => !!auth.currentUser && auth.currentUser.uid === uid;
  followingMerge = (async () => {
    try {
      if (!stillOn()) return "followed";
      const snap = await getDocFromServer(doc(db, "accountMerges", uid));
      const record = snap.exists() ? snap.data() : null;
      const followable = !!record &&
        ["deleting", "done", "purged"].includes(record.status);
      if (!stillOn()) return "followed";
      if (!followable) return "not-merged";
      const result = await callable("resumeMergedSession")({});
      const data = (result && result.data) || {};
      if (!stillOn() || data.uid === uid) return "followed";
      if (!data.customToken) return "error";
      markSeatPending("merge");
      await signInWithCustomToken(auth, data.customToken);
      return "followed";
    } catch (err) {
      console.warn("follow merged account failed:", err);
      return stillOn() ? "error" : "followed";
    }
  })();
  followingMerge.finally(() => { followingMerge = null; });
  return followingMerge;
}

/**
 * This browser's device record vanished: another session ended this one
 * (sign out here), or the account was merged into another (follow it).
 * Never signs out of an account the browser has already moved to.
 */
async function handleLocalRecordRemoved(userID, attempt = 0) {
  const outcome = await followMergeIfNeeded(userID);
  if (!auth.currentUser || auth.currentUser.uid !== userID) return;
  if (outcome === "not-merged") {
    forceSignOut("device record removed remotely", userID);
  } else if (outcome === "error") {
    if (attempt < 3) {
      setTimeout(() => handleLocalRecordRemoved(userID, attempt + 1), 5000 * (attempt + 1));
    } else {
      forceSignOut("device record removed remotely", userID);
    }
  }
}

function attachUserListener(userID) {
  detachUserListeners();

  const userRef = doc(db, "users", userID);
  userDocUnsub = onSnapshot(
    userRef,
    (snapshot) => {
      if (!snapshot.exists()) {
        state.userData = null;
        // Gone for real: if the account was merged into another one, this
        // browser follows it.
        if (!snapshot.metadata.fromCache) followMergeIfNeeded(userID);
        return;
      }
      const data = snapshot.data();
      if (!data || !data.userID) {
        state.userData = null;
        return;
      }
      const notifications = Array.isArray(data.notifications) ? [...data.notifications] : [];
      if ((!data.name || data.name.length === 0) && !notifications.includes("NAME_MISSING")) {
        notifications.push("NAME_MISSING");
      }
      if (data.friendOnboarded == null && !notifications.includes("FRIENDS_ONBOARDED")) {
        notifications.push("FRIENDS_ONBOARDED");
      }
      state.userData = { ...data, notifications };
      applySubcollections();
      // May this browser use the account? (One device at a time for a
      // free account; asked again when premium changes.)
      updateSeat(userID, data.premium === true);
      // COPPA / RGPD-K enforcement runs once the user document is
      // hydrated. Under-13 accounts are signed out immediately; kid-mode
      // (13–15) accounts keep using the app but with analytics fully
      // disabled. See `utils/trackingPolicy.js`.
      enforceAgePolicy(data);
    },
    (err) => {
      console.error("user doc listener error:", err);
      if (isAuthLossError(err)) forceSignOut("user-doc listener");
    },
  );

  const devicesRef = collection(db, "users", userID, "devices");
  hasSeenLocalDeviceRecord = false;
  const localDeviceID = deviceLinkID();
  console.info("[devices] watching", { localDeviceID });
  devicesUnsub = onSnapshot(
    devicesRef,
    (snap) => {
      latestDevices = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      if (state.userData) {
        state.userData = { ...state.userData, devices: latestDevices };
      }
      const hasLocal = latestDevices.some(
        (d) => d.id === localDeviceID || d.deviceID === localDeviceID,
      );
      console.info("[devices] snapshot", {
        ids: latestDevices.map((d) => d.id),
        localDeviceID,
        hasLocal,
        hasSeenLocalDeviceRecord,
      });
      if (hasLocal) {
        hasSeenLocalDeviceRecord = true;
      } else if (hasSeenLocalDeviceRecord) {
        // The record was here a moment ago and is now gone — another session
        // (e.g. the iOS app's "End session" action) revoked this browser, or
        // the account was merged into another one (its records moved with
        // it). Stop the heartbeat first so it can't immediately recreate
        // the doc.
        stopLastConnectionTimer();
        handleLocalRecordRemoved(userID);
      }
    },
    (err) => {
      console.error("devices listener error:", err);
      if (isAuthLossError(err)) forceSignOut("devices listener");
    },
  );

  const friendsRef = collection(db, "users", userID, "friends");
  friendsUnsub = onSnapshot(
    friendsRef,
    (snap) => {
      latestFriends = snap.docs
        .map((d) => d.data())
        .filter((f) => f && f.friendID && f.name && f.inviteUserID)
        .map((f) => ({
          friendID: f.friendID,
          name: f.name,
          inviteUserID: f.inviteUserID,
        }));
      if (state.userData) {
        state.userData = { ...state.userData, friends: latestFriends };
      }
    },
    (err) => {
      console.error("friends listener error:", err);
      if (isAuthLossError(err)) forceSignOut("friends listener");
    },
  );

  const sharedNotesRef = collection(db, "users", userID, "sharedNotes");
  sharedNotesUnsub = onSnapshot(
    sharedNotesRef,
    (snap) => {
      latestSharedNotes = snap.docs
        .map((d) => d.data())
        .filter((n) => n && n.noteID && n.userID && n.name)
        .map((n) => ({ noteID: n.noteID, userID: n.userID, name: n.name }));
      if (state.userData) {
        state.userData = { ...state.userData, sharedNotes: latestSharedNotes };
      }
    },
    (err) => {
      console.error("sharedNotes listener error:", err);
      if (isAuthLossError(err)) forceSignOut("sharedNotes listener");
    },
  );
}

export function initAuth() {
  if (initialized) return;
  initialized = true;

  onAuthStateChanged(auth, async (user) => {
    state.currentUser = user;
    state.isAuthenticated = !!user;
    state.isLoading = false;

    if (user) {
      // Only enable analytics for authenticated users who already accepted.
      // The actual UID -> Analytics binding waits until the user document is
      // hydrated AND `setTrackingEligible(true)` flips the COPPA/RGPD-K gate
      // (see `enforceAgePolicy`). Without that gate a freshly-signed-in
      // minor would have one UID ping sent before the user doc snapshot
      // lands.
      const consent = (() => {
        try { return typeof localStorage !== "undefined" && localStorage.getItem("cogito_cookie_consent"); }
        catch (_) { return null; }
      })();
      if (consent === "accepted") {
        // setAnalyticsUser short-circuits while trackingEligible !== true,
        // so this is safe to call before the age gate runs — it'll bind the
        // UID later via the enforceAgePolicy path once the user doc loads.
        enableAnalytics().then(() => setAnalyticsUser(user.uid));
      }
      // If consent is undecided the CookieBanner will appear and acceptCookies()
      // will call enableAnalytics() + setAnalyticsUser() at that point.
    } else {
      // Reset the under-13 guard so a future re-login from a different
      // account doesn't keep swallowing the sign-out flow.
      under13SignOutInFlight = false;
      // Fail-closed: forget tracker eligibility until the next user doc
      // tells us otherwise.
      setTrackingEligible(false);
      disableAnalytics();
      setAnalyticsUser(null);
    }

    if (!user) {
      // Auth flipped to `null`. If we previously had a user and this wasn't
      // triggered by our own `signOut()` (e.g. cookies expired, IndexedDB
      // was cleared, the account was disabled, the password was changed
      // elsewhere…) treat it as an expired session: try to remove this
      // device from the user's device list — best-effort, since the auth
      // token is already gone — then redirect to /signin.
      if (previousUid && !isExplicitSignOut) {
        try {
          const deviceID = deviceLinkID();
          await deleteDoc(doc(db, "users", previousUid, "devices", deviceID));
        } catch (_) {
          // Token is invalid by now — Firestore rules will reject the write.
          // We tried; the device row will be picked up the next time the
          // user signs in or by a future cleanup pass.
        }
        if (typeof window !== "undefined") {
          const path = window.location.pathname || "";
          const isPublic = path === "/" || path.startsWith("/signin") ||
            path.startsWith("/terms") || path.startsWith("/privacy") ||
            path.startsWith("/legal") || path.startsWith("/help") ||
            path.startsWith("/compliance");
          if (!isPublic) {
            window.location.replace("/signin");
          }
        }
      }
      isExplicitSignOut = false;
      previousUid = null;
      state.userData = null;
      resetSeat();
      detachUserListeners();
      detachNotesListeners();
      stopLastConnectionTimer();
      try {
        localStorage.removeItem("userID");
      } catch (_) {
        // ignore
      }
      return;
    }

    previousUid = user.uid;
    try {
      localStorage.setItem("userID", user.uid);
    } catch (_) {
      // ignore
    }
    attachUserListener(user.uid);
    attachNotesListeners(user.uid);
    startLastConnectionTimer(user.uid);
  });
}

export async function signOut() {
  // Mirrors the iOS sign-out cleanup at DeviceView.swift:304-358:
  //   1. delete this device's record so it disappears from the user's device list,
  //   2. detach Firestore listeners + heartbeat (the AuthViewModel willSignOut path),
  //   3. clear local session state,
  //   4. signOut from Firebase Auth.
  const user = auth.currentUser;
  const uid = user ? user.uid : null;

  // Mark this so the auth-state observer doesn't double-handle the imminent
  // null transition as an implicit session expiry.
  isExplicitSignOut = true;

  // While still signed in: frees the account for another device when this
  // browser held it (a no-op otherwise).
  await releaseSeat();

  if (uid) {
    try {
      const deviceID = deviceLinkID();
      await deleteDoc(doc(db, "users", uid, "devices", deviceID));
    } catch (err) {
      console.warn("signOut: device doc delete failed:", err);
    }
  }

  detachUserListeners();
  detachNotesListeners();
  stopLastConnectionTimer();

  try {
    localStorage.removeItem("userID");
  } catch (_) {
    // ignore quota/permission errors
  }

  await fbSignOut(auth);
}

export const authState = readonly(state);

// Helper for route guards / one-shot checks.
export function waitForAuth() {
  if (!state.isLoading) return Promise.resolve(state.currentUser);
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      if (!state.isLoading) {
        clearInterval(interval);
        resolve(state.currentUser);
      }
    }, 30);
  });
}

// Resolves once the Firestore user-doc listener has populated `state.userData`,
// or `timeoutMs` has elapsed. Needed because Firebase auth state and the user
// doc snapshot arrive on separate channels — the auth promise resolves before
// the doc snapshot fires, and post-signin redirects that read `userData`
// immediately get `null` (sending QR-paired users through /onboarding).
export function waitForUserData(timeoutMs = 5000) {
  if (state.userData) return Promise.resolve(state.userData);
  if (!state.isAuthenticated) return Promise.resolve(null);
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const interval = setInterval(() => {
      if (state.userData) {
        clearInterval(interval);
        resolve(state.userData);
      } else if (!state.isAuthenticated) {
        clearInterval(interval);
        resolve(null);
      } else if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(interval);
        resolve(null);
      }
    }, 30);
  });
}

// Re-export for other modules
export { serverTimestamp };
